'use client';

import type { SearchResponse } from '@algolia/client-search';

import { RadioGroupComponent } from '@/modules/common/components/Radio/RadioGroupComponent';
import {
  AccordionContent,
  AccordionItem,
  AccordionTrigger,
} from '@/modules/common/components/ui/Accordion';
import { useQueryParams } from '@/modules/common/hooks/useQueryParams';

import type { Filter } from '../../services/filtersTransform';
import { PLP_VIEWBY_OPTIONS } from '../samples/constants';
import { SERPSearchBarSimplified } from '../search/SERPSearchBarSimplified';
import { SamplesFiltersAccordionSimplified } from './SamplesFiltersAccordionSimplified';

type SamplesLeftSidebarSimplifiedProps = {
  facets?: Record<string, Record<string, number>>;
  /** Algolia renderingContent from the search response — used for facet ordering. */
  renderingContent?: SearchResponse['renderingContent'];
  /** CMS per-page filter override list (from availableFiltersCollection). */
  mappedFilters?: Filter[];
};

export const SamplesLeftSidebarSimplified = ({
  facets = {},
  renderingContent,
  mappedFilters,
}: SamplesLeftSidebarSimplifiedProps) => {
  const [viewBy, setViewBy] = useQueryParams<string>('viewBy', 'main');

  const viewByControl = {
    items: PLP_VIEWBY_OPTIONS.map(({ label, value }) => ({ label, value })),
    handleChange: (value: string) => setViewBy(value),
    selectedValue: viewBy || ('main' as string),
  };

  return (
    <div className="px-3 py-4" data-testid="samples-left-sidebar">
      <SERPSearchBarSimplified className="mb-3 flex-1" testIdPrefix="serp-sidebar" />

      <SamplesFiltersAccordionSimplified
        facets={facets}
        renderingContent={renderingContent}
        mappedFilters={mappedFilters}
      >
        <AccordionItem
          value="view-by"
          className="border-b border-gray-100 py-3 md:border-b md:border-gray-100 md:py-1"
          data-testid="view-by-accordion-item"
        >
          <AccordionTrigger
            data-testid="view-by-trigger"
            className="md:text-[14px] lg:text-[16px]"
            headerTag="p"
          >
            Display as
          </AccordionTrigger>
          <AccordionContent data-testid="view-by-content">
            <RadioGroupComponent {...viewByControl} data-testid="view-by-radio-group" />
          </AccordionContent>
        </AccordionItem>
      </SamplesFiltersAccordionSimplified>
    </div>
  );
};
